import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { Pencil } from 'lucide-react'

const ENTITY_IDENTITY_CARD_BASE_CLASS =
  'group/section relative overflow-visible transition-all duration-300'

export function entityIdentityCardPositionClass(coverEnabled: boolean) {
  return coverEnabled ? 'z-10 !mt-1.5' : 'z-0 mt-[60px]'
}

type EntityIdentityCardProps = {
  coverEnabled?: boolean
  className?: string
  highlightClassName?: string
  agentHighlight?: string
  media: ReactNode
  onEdit?: () => void
  editAriaLabel?: string
  editTitle?: string
  editDisabled?: boolean
  /** When false, edit control stays visible (e.g. greenfield org). Default: show on hover/focus. */
  editShowOnHover?: boolean
  children: ReactNode
}

/** Shared identity card shell for person and organisation private profile heroes. */
export function EntityIdentityCard({
  coverEnabled = false,
  className,
  highlightClassName,
  agentHighlight,
  media,
  onEdit,
  editAriaLabel = 'Edit',
  editTitle = 'Edit',
  editDisabled = false,
  editShowOnHover = true,
  children,
}: EntityIdentityCardProps) {
  return (
    <Card
      className={cn(
        ENTITY_IDENTITY_CARD_BASE_CLASS,
        entityIdentityCardPositionClass(coverEnabled),
        highlightClassName,
        className,
      )}
      {...(agentHighlight ? { 'data-agent-highlight': agentHighlight } : {})}
    >
      {media}
      <CardHeader showTitle={false}>
        {onEdit ? (
          <CardAction>
            <Button
              type="button"
              variant="ghostToolbar"
              size="iconSm"
              className={cn(
                'shrink-0',
                editShowOnHover
                  ? 'opacity-100 transition-opacity md:opacity-0 md:group-hover/section:opacity-100 focus-visible:opacity-100'
                  : undefined,
              )}
              aria-label={editAriaLabel}
              title={editTitle}
              onClick={onEdit}
              disabled={editDisabled}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </CardAction>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  )
}

type EntityIdentityCardMediaProps = {
  variant: 'avatar' | 'logo'
  children: ReactNode
  className?: string
  agentHighlight?: string
}

/** Overlapping hero media — round avatar or squared org logo. */
export function EntityIdentityCardMedia({
  variant: _variant,
  children,
  className,
  agentHighlight,
}: EntityIdentityCardMediaProps) {
  return (
    <div
      className={cn(
        'absolute left-4 top-0 z-[2] size-[120px] shrink-0 -translate-y-1/2',
        className,
      )}
      {...(agentHighlight ? { 'data-agent-highlight': agentHighlight } : {})}
    >
      {children}
    </div>
  )
}

type EntityIdentityCardBodyProps = {
  children: ReactNode
  className?: string
}

/** Body below overlapping media — consistent top inset and vertical rhythm. */
export function EntityIdentityCardBody({ children, className }: EntityIdentityCardBodyProps) {
  return (
    <div className={cn('min-w-0 w-full space-y-4 pt-[72px]', className)}>{children}</div>
  )
}

type EntityIdentityCardMetadataProps = {
  children: ReactNode
  className?: string
}

/** Metadata + contact cluster below the identity title — uniform `space-y-2` between rows. */
export function EntityIdentityCardMetadata({
  children,
  className,
}: EntityIdentityCardMetadataProps) {
  return <div className={cn('space-y-2', className)}>{children}</div>
}

type EntityIdentityCardTitleProps = {
  children: ReactNode
  /** Status pill sitting on the same line as the name. */
  badge?: ReactNode
}

export function EntityIdentityCardTitle({ children, badge }: EntityIdentityCardTitleProps) {
  return (
    <h1 className="flex flex-wrap items-center gap-2 text-xl font-semibold leading-tight text-foreground">
      <span className="min-w-0">{children}</span>
      {badge}
    </h1>
  )
}
